import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { PageShell } from "~/components/PageShell"
import { assetUrl } from "~/utils/assetUrl"

export const DEFAULT_PHRASE_INTERVAL_MS = 2000

const phraseFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

export interface AnimatedProcessingScreenProps {
  phrases: string[]
  footerText?: string
  phraseIntervalMs?: number
}

export function AnimatedProcessingScreen({
  phrases,
  footerText,
  phraseIntervalMs = DEFAULT_PHRASE_INTERVAL_MS,
}: AnimatedProcessingScreenProps) {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length)
    }, phraseIntervalMs)
    return () => clearInterval(interval)
  }, [phrases.length, phraseIntervalMs])

  return (
    <PageShell centerContent>
      <motion.div
        className="flex flex-col items-center gap-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="relative flex items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-[var(--color-gold-400)]"
            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="relative flex h-28 w-28 items-center justify-center"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <img
              src={assetUrl("/images/swish.png")}
              alt=""
              className="h-full w-full object-contain"
              aria-hidden
            />
          </motion.div>
        </div>

        <div className="flex min-h-[3rem] items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentPhraseIndex}
              variants={phraseFade}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="text-center text-lg font-medium text-[var(--color-text-primary)]"
            >
              {phrases[currentPhraseIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        {footerText && (
          <p className="text-sm text-[var(--color-text-secondary)]">{footerText}</p>
        )}
      </motion.div>
    </PageShell>
  )
}
